<template>
    <div
        v-if="hasLabels"
        ref="labelsContainer"
        class="shipping-method-labels d-flex align-items-center"
        :class="{ 'single-line': singleLine }"
    >
        <div
            v-for="(label, index) in shippingConstraintLabels"
            :key="label.key"
            v-show="!singleLine || index === 0 || index <= visibleTailCount"
            :class="['list-box-item', labelClass(label.key)]"
        >
            {{ singleLine && index === 0 ? firstChipDisplayText : getLabelText(label) }}
        </div>
        <div
            v-if="singleLine && remainingCount > 0"
            :class="['list-box-item', 'plus-badge', { unavailable: hasUnavailableHidden }]"
        >
            +{{ remainingCount }}
        </div>
    </div>
</template>

<script>
import { formatPrice } from "@/v2/utils/utilities.js";

/**
 * Presentational tag/label row for a single shipping method. Shared by bo-shipping-method-select
 * (the dropdown) and the read-only history views so the tags always look identical.
 *
 * Colouring comes from the method's own `failedLabels` map and `disabled` flag (set by the
 * shipping-method mixin). A plain method object with neither — e.g. a saved method on a history
 * page — renders every tag in the neutral "available" style.
 */
export default {
    name: "bo-shipping-method-labels",
    props: {
        method: {
            type: Object,
            required: true,
        },
        translations: {
            type: Object,
            default: () => ({}),
        },
        currencySymbol: {
            type: String,
            default: "",
        },
        decimal: {
            type: [Number, String],
            default: 2,
        },
        getAllShippingClasses: {
            type: Function,
            default: () => "",
        },
        getShippingMethodMinTableRateLabel: {
            type: Function,
            default: () => "",
        },
        getShippingMethodMaxTableRateLabel: {
            type: Function,
            default: () => "",
        },
        shippingMethodUses: {
            type: Object,
            default: () => ({}),
        },
        singleLine: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            visibleTailCount: Infinity,
            remainingCount: 0,
            firstChipDisplayText: "",
        };
    },
    computed: {
        remainingUses() {
            return this.shippingMethodUses[this.method.id]?.remaining_uses ?? null;
        },
        remainingUsesLabel() {
            const template = this.translations.remaining_available ?? ":remaining Available";
            return template.replace(":remaining", this.remainingUses);
        },
        shippingConstraintLabels() {
            const labels = [];
            if (this.getAllShippingClasses(this.method.shipping_classes) != "") {
                labels.push({ key: "shipping_class" });
            }
            if (this.getShippingMethodMinTableRateLabel(this.method) != "") {
                labels.push({ key: "min_table_rate" });
            }
            if (this.getShippingMethodMaxTableRateLabel(this.method) != "") {
                labels.push({ key: "max_table_rate" });
            }
            if (this.method.min_order_amount > 0) {
                labels.push({ key: "min_spend" });
            }
            if (this.method.max_order_amount > 0) {
                labels.push({ key: "max_spend" });
            }
            if (this.remainingUses != null) {
                labels.push({ key: "remaining" });
            }
            return labels;
        },
        hasLabels() {
            return this.shippingConstraintLabels.length > 0;
        },
        // If any of the chips currently hidden behind the "+N" badge are themselves failing
        // (red/"unavailable"), flag the badge red too so that state isn't silently swallowed.
        hasUnavailableHidden() {
            if (this.remainingCount === 0) {
                return false;
            }
            const labels = this.shippingConstraintLabels;
            const hiddenLabels = labels.slice(labels.length - this.remainingCount);
            return hiddenLabels.some((label) => this.labelClass(label.key) === "unavailable");
        },
    },
    watch: {
        shippingConstraintLabels() {
            if (this.singleLine) {
                this.$nextTick(this.updateVisibleChips);
            }
        },
    },
    mounted() {
        if (!this.singleLine) {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => this.updateVisibleChips());
        if (this.$refs.labelsContainer) {
            this.resizeObserver.observe(this.$refs.labelsContainer);
        }

        this.updateVisibleChips();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        formatPrice,
        // "muted" for disabled (incompatible) rows; "unavailable" (red) for the specific failing
        // label on a selectable row; "available" (neutral gray) otherwise.
        labelClass(labelType) {
            if (this.method.disabled) {
                return "muted";
            }
            return this.method.failedLabels && this.method.failedLabels[labelType] ? "unavailable" : "available";
        },
        // Single source of truth for a label's text — mirrors each template branch exactly,
        // so the width we measure here always matches what actually renders.
        getLabelText(label) {
            switch (label.key) {
                case "shipping_class":
                    return (
                        (this.translations.shipping_class ?? "Shipping Class") +
                        ": " +
                        this.getAllShippingClasses(this.method.shipping_classes)
                    );
                case "min_table_rate":
                    return this.getShippingMethodMinTableRateLabel(this.method);
                case "max_table_rate":
                    return this.getShippingMethodMaxTableRateLabel(this.method);
                case "min_spend":
                    return `${this.translations.min_spend ?? "Min. Spend"}: ${this.currencySymbol} ${formatPrice(this.method.min_order_amount, this.decimal)}`;
                case "max_spend":
                    return `${this.translations.max_spend ?? "Max. Spend"}: ${this.currencySymbol} ${formatPrice(this.method.max_order_amount, this.decimal)}`;
                case "remaining":
                    return this.remainingUsesLabel;
                default:
                    return "";
            }
        },
        getTextWidth(text, font) {
            const canvas = this.getTextWidth.canvas || (this.getTextWidth.canvas = document.createElement("canvas"));
            const context = canvas.getContext("2d");
            context.font = font;
            return context.measureText(text).width;
        },
        // Binary-searches the longest prefix of `text` (plus an ellipsis) whose measured width
        // fits within `maxWidth`. Width grows monotonically with prefix length, so this is safe.
        truncateToFit(text, font, maxWidth) {
            const ellipsis = "…";
            if (this.getTextWidth(text, font) <= maxWidth) {
                return text;
            }

            let lo = 0;
            let hi = text.length;
            let result = ellipsis;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                const candidate = text.slice(0, mid) + ellipsis;
                if (this.getTextWidth(candidate, font) <= maxWidth) {
                    result = candidate;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            return result;
        },
        updateVisibleChips() {
            if (!this.singleLine) {
                return;
            }

            const container = this.$refs.labelsContainer;
            if (!container || container.clientWidth === 0) {
                // Not laid out yet (e.g. v-if="hasLabels" root just mounted) — retry next frame.
                // This is the only async dependency left; the width math itself is synchronous.
                requestAnimationFrame(() => this.updateVisibleChips());
                return;
            }

            const labels = this.shippingConstraintLabels;
            if (labels.length === 0) {
                this.visibleTailCount = 0;
                this.remainingCount = 0;
                this.firstChipDisplayText = "";
                return;
            }

            const containerWidth = container.clientWidth;
            const font = window.getComputedStyle(container).font || "12px Montserrat, sans-serif";

            // Chip chrome from .list-box-item (frontend.scss/master.scss): padding: 2px 8px
            // (16px) + border: 1px each side (2px) + the row's 4px gap between chips. The "+N"
            // badge has no border.
            const chipChrome = 16 + 2 + 4;
            const badgeChrome = 16 + 4;

            const firstFullText = this.getLabelText(labels[0]);
            const tailWidths = labels
                .slice(1)
                .map((label) => this.getTextWidth(this.getLabelText(label), font) + chipChrome);
            const badgeWidth = this.getTextWidth(`+${tailWidths.length}`, font) + badgeChrome;

            const firstChipWidth = this.getTextWidth(firstFullText, font) + chipChrome;

            // Chip 0 normally renders in full — it's reserved before anything else competes for
            // space. Only in the rare case its own text alone is wider than the entire row does
            // it ellipsis-truncate, leaving room for the "+N" badge (if there's anything to hide).
            if (firstChipWidth > containerWidth) {
                const reserveForBadge = tailWidths.length > 0 ? badgeWidth : 0;
                const textBudget = containerWidth - reserveForBadge - chipChrome;
                this.firstChipDisplayText = this.truncateToFit(firstFullText, font, textBudget);
                this.visibleTailCount = 0;
                this.remainingCount = tailWidths.length;
                return;
            }

            this.firstChipDisplayText = firstFullText;

            // Chip 0 is always reserved in full — only tail chips compete for the remaining
            // space, one at a time, in order. The first one that doesn't fit (and everything
            // after it) collapses into the "+N" badge. Whole chips only, computed synchronously
            // from known text/font — no DOM measurement, no render-timing race.
            const countFitting = (budget) => {
                let widthSoFar = firstChipWidth;
                let count = 0;
                for (let i = 0; i < tailWidths.length; i++) {
                    if (widthSoFar + tailWidths[i] > budget) {
                        break;
                    }
                    widthSoFar += tailWidths[i];
                    count = i + 1;
                }
                return count;
            };

            // Pass 1: see if every tail chip fits without needing a badge at all.
            let visibleTailCount = countFitting(containerWidth);
            if (visibleTailCount === tailWidths.length) {
                this.visibleTailCount = visibleTailCount;
                this.remainingCount = 0;
                return;
            }

            // Pass 2: a badge is needed — redo the fit with its width reserved, so the last
            // fitting tail chip doesn't crowd out room for the badge itself.
            visibleTailCount = countFitting(containerWidth - badgeWidth);
            this.visibleTailCount = visibleTailCount;
            this.remainingCount = tailWidths.length - visibleTailCount;
        },
    },
};
</script>
